import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { cn } from './cn';
import { clientColor, initialsOf } from './colors';

/** Section header: uppercase label on the left, an optional plain value or action on the right. */
/** Uppercase, except the hours unit: "TIME · 31.0 h", never "31.0 H". */
function upper(node: ReactNode): ReactNode {
  return typeof node === 'string' ? node.toUpperCase().replace(/(\d) H\b/g, '$1 h') : node;
}

export function SectionHeader({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 text-caption font-medium text-[var(--text-muted)]">
      <span className="truncate tracking-[0.02em]">{upper(children)}</span>
      {right && <span className="tnum shrink-0">{right}</span>}
    </div>
  );
}

/** A grouped list: optional header and footer, rows in one rounded card with inset hairlines. */
export function Group({
  header,
  headerRight,
  footer,
  children,
  className
}: {
  header?: ReactNode;
  headerRight?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('flex flex-col gap-2', className)}>
      {(header || headerRight) && <SectionHeader right={headerRight}>{header}</SectionHeader>}
      <div className="group overflow-hidden rounded-2xl bg-[var(--surface)]">{children}</div>
      {footer && <p className="px-4 text-caption text-[var(--text-muted)]">{footer}</p>}
    </section>
  );
}

/** White card for anything that is not a list (summary, chart, form block). */
export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('rounded-2xl bg-[var(--surface)] p-4', className)}>{children}</div>;
}

export function ColorBar({ id }: { id: string }) {
  return <span className="h-9 w-1 shrink-0 rounded-sm" style={{ background: clientColor(id).dot }} />;
}

export function ColorDot({ id, className }: { id: string; className?: string }) {
  return <span className={cn('size-2.5 shrink-0 rounded-full', className)} style={{ background: clientColor(id).dot }} />;
}

export function Avatar({ id, name, size = 36, dark }: { id: string; name: string | null | undefined; size?: number; dark?: boolean }) {
  const c = clientColor(id);
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full font-semibold',
        dark ? 'bg-[var(--primary)] text-[var(--primary-fg)]' : 'bg-[var(--av-bg)] text-[var(--av-fg)] dark:bg-[var(--av-dbg)] dark:text-[var(--av-dfg)]'
      )}
      style={
        {
          width: size,
          height: size,
          fontSize: Math.round(size * 0.38),
          '--av-bg': c.bg,
          '--av-fg': c.fg,
          '--av-dbg': c.darkBg,
          '--av-dfg': c.darkFg
        } as CSSProperties
      }
    >
      {initialsOf(name)}
    </span>
  );
}

interface RowProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Colour bar, dot, avatar or icon. Moves the hairline past it. */
  leading?: ReactNode;
  /** Hairline inset when there is a leading element (px from the card edge). */
  inset?: number;
  trailing?: ReactNode;
  detail?: ReactNode;
  to?: string;
  onClick?: () => void;
  chevron?: boolean;
  destructive?: boolean;
  accent?: boolean;
  className?: string;
}

/**
 * The one list row: 52 tall, 64 with a subtitle. A `to` or `onClick` makes it
 * tappable. `detail` is the grey value of a settings row; `trailing` is
 * anything else (amount, badge, switch).
 */
export function Row({ title, subtitle, leading, inset, trailing, detail, to, onClick, chevron, destructive, accent, className }: RowProps) {
  const body = (
    <>
      {leading}
      <div className={cn('flex min-w-[35%] flex-1 flex-col gap-0.5', subtitle ? 'py-3' : 'py-3.5')}>
        <div className={cn('truncate text-body', destructive && 'text-[var(--danger)]', accent && 'font-medium text-[var(--accent)]')}>{title}</div>
        {subtitle && <div className="truncate text-secondary text-[var(--text-muted)]">{subtitle}</div>}
      </div>
      {detail !== undefined && detail !== null && <div className="min-w-0 truncate text-right text-body text-[var(--text-muted)]">{detail}</div>}
      {trailing && <div className="flex shrink-0 items-center">{trailing}</div>}
      {(chevron ?? Boolean(to)) && <ChevronRight className="size-4.5 shrink-0 text-[var(--text-subtle)]" strokeWidth={2.4} />}
    </>
  );
  const cls = cn(
    'flex w-full items-center gap-3 px-4 text-left',
    subtitle ? 'min-h-16' : 'min-h-[52px]',
    (to || onClick) && 'active:bg-[var(--pressed)]',
    className
  );
  const style = (leading ? { '--inset': `${inset ?? 64}px` } : undefined) as CSSProperties | undefined;
  if (to) return <Link to={to} className={cls} style={style}>{body}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className={cls} style={style}>{body}</button>;
  return <div className={cls} style={style}>{body}</div>;
}

/** Label left, value right: the read-only half of a form. */
export function ValueRow({ label, value, onClick }: { label: ReactNode; value: ReactNode; onClick?: () => void }) {
  return <Row title={label} detail={value} onClick={onClick} chevron={Boolean(onClick)} />;
}
